<template>
  <div
    v-if="status?.enabled"
    class="ws-assistant"
    :class="{ 'ws-assistant--embedded': embedded }"
  >
    <v-btn
      v-if="!open"
      class="ws-assistant-launcher text-none"
      color="primary"
      size="large"
      rounded="xl"
      prepend-icon="mdi-chat-question-outline"
      elevation="6"
      aria-label="Open the support assistant"
      @click="open = true"
    >Ask AI assistant</v-btn>

    <v-card
      v-else
      class="ws-assistant-panel d-flex flex-column"
      elevation="12"
      role="dialog"
      aria-label="WireSock support assistant"
      :style="embedded ? undefined : panelStyle"
    >
      <!-- The panel is anchored bottom-right, so it grows from the top-left corner. -->
      <div
        v-if="!embedded"
        class="ws-assistant-resize"
        title="Drag to resize, double-click to reset"
        aria-hidden="true"
        @pointerdown="startResize"
        @dblclick="resetSize"
      />
      <v-card-title class="d-flex align-center py-2 pr-1">
        <v-icon
          icon="mdi-robot-outline"
          size="small"
          class="mr-2"
        />
        <span class="text-subtitle-1">WireSock AI assistant</span>
        <v-spacer />
        <v-btn
          v-if="messages.length > 0"
          icon="mdi-refresh"
          variant="text"
          size="small"
          title="New conversation"
          aria-label="New conversation"
          @click="reset"
        />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close"
          @click="close"
        />
      </v-card-title>
      <v-divider />

      <div
        ref="scroller"
        class="ws-assistant-body flex-grow-1 pa-3"
      >
        <!-- AI disclosure on first contact (FR-001, FR-013, EU AI Act Art. 50) -->
        <div class="ws-assistant-disclosure text-body-2 mb-3">
          <p class="mb-2">
            <strong>You are chatting with an AI assistant.</strong>
            It answers from the WireSock documentation and support knowledge base, and its answers may contain mistakes.
          </p>
          <p class="mb-2">
            Please don't share license keys, private keys, passwords or personal data in this chat.
          </p>
          <p class="mb-0">
            Need a person?
            <a
              href="#"
              @click.prevent="showEscalation = true"
            >Talk to a human</a>
            ·
            <nuxt-link
              to="/about/support-assistant-privacy"
              target="_blank"
            >How we handle chat data</nuxt-link>
          </p>
        </div>

        <assistant-message
          v-for="message in messages"
          :key="message.localId"
          :message="message"
          :rating="message.messageId ? ratings[message.messageId] : null"
          @human="showEscalation = true"
          @rate="rating => rate(message, rating)"
          @report="openReport(message)"
        />

        <assistant-escalation-form
          v-if="showEscalation"
          :conversation-id="conversationId"
          :conversation-token="conversationToken"
          class="mb-3"
          @close="showEscalation = false"
        />
      </div>

      <v-divider />
      <form
        class="pa-2"
        @submit.prevent="submit"
      >
        <v-textarea
          v-model="draft"
          :counter="maxChars"
          :maxlength="maxChars"
          placeholder="Ask about WireSock…"
          rows="1"
          max-rows="5"
          auto-grow
          variant="outlined"
          density="compact"
          hide-details="auto"
          :disabled="sending"
          @keydown.enter.exact.prevent="submit"
        >
          <template #append-inner>
            <v-btn
              icon="mdi-send"
              variant="text"
              size="small"
              :disabled="!draft.trim() || sending"
              aria-label="Send"
              @click.stop="submit"
            />
          </template>
        </v-textarea>
      </form>
    </v-card>

    <v-dialog
      v-model="report.open"
      max-width="420"
    >
      <v-card title="Report a wrong answer">
        <v-card-text>
          <v-textarea
            v-model="report.comment"
            label="What was wrong? (optional)"
            counter="1000"
            maxlength="1000"
            rows="3"
            variant="outlined"
          />
          <div class="text-caption text-grey">Please don't include license keys or personal data.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            @click="report.open = false"
          >Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="report.sending"
            @click="sendReport"
          >Send report</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script
  setup
  lang="ts"
>
import AssistantMessage from '~/components/support-assistant/AssistantMessage.vue';
import AssistantEscalationForm from '~/components/support-assistant/AssistantEscalationForm.vue';
import { useAssistantChat, type AssistantChatMessage, type AssistantStatus } from '~/composables/useAssistantChat';

const props = defineProps<{
  /**
   * Inside the iframe of the documentation site (pages/support-assistant/embed.vue): always open,
   * fills the frame; "close" asks the parent page to hide the frame.
   */
  embedded?: boolean;
}>();

/** Message the documentation page listens for (docs/.vitepress/components/WiresockAssistant.vue). */
const EMBED_CLOSE_MESSAGE = 'ws-assistant:close';

const auth = useAuth();
const { messages, sending, conversationId, conversationToken, send, reset } = useAssistantChat();
const { data: status } = useFetch<AssistantStatus>('/api/assistant/status', { server: false, lazy: true });

const open = ref(props.embedded === true);
const draft = ref('');
const showEscalation = ref(false);
const scroller = ref<HTMLElement | null>(null);
const ratings = reactive<Record<string, 'up' | 'down'>>({});
const report = reactive({ open: false, comment: '', messageId: '', sending: false });
const maxChars = computed(() => status.value?.limits.messageMaxChars ?? 2000);

function close(): void {
  if (!props.embedded) {
    open.value = false;
    return;
  }
  window.parent.postMessage({ type: EMBED_CLOSE_MESSAGE }, window.location.origin);
}

async function submit(): Promise<void> {
  const text = draft.value;
  if (!text.trim() || sending.value) {
    return;
  }
  draft.value = '';
  await send(text);
}

async function postFeedback(body: { messageId: string; rating: 'up' | 'down'; reportedWrong?: boolean; comment?: string }): Promise<void> {
  await $fetch('/api/assistant/feedback', {
    method: 'POST',
    body: { ...body, conversationToken: conversationToken.value },
    headers: { 'X-CSRF-Token': (await auth.getCsrfToken()) ?? '' },
  });
}

async function rate(message: AssistantChatMessage, rating: 'up' | 'down'): Promise<void> {
  if (!message.messageId) {
    return;
  }
  ratings[message.messageId] = rating;
  try {
    await postFeedback({ messageId: message.messageId, rating });
  } catch {
    // Feedback is best-effort; the rating stays visible locally.
  }
}

function openReport(message: AssistantChatMessage): void {
  if (!message.messageId) {
    return;
  }
  report.messageId = message.messageId;
  report.comment = '';
  report.open = true;
}

async function sendReport(): Promise<void> {
  report.sending = true;
  try {
    await postFeedback({ messageId: report.messageId, rating: 'down', reportedWrong: true, comment: report.comment.trim() || undefined });
    ratings[report.messageId] = 'down';
    report.open = false;
  } catch {
    report.open = false;
  } finally {
    report.sending = false;
  }
}

const SIZE_KEY = 'ws-assistant-size';
const MIN_WIDTH = 320;
const MIN_HEIGHT = 400;
const panelSize = ref<{ width: number; height: number } | null>(readSavedSize());
const panelStyle = computed(() => panelSize.value
  ? { width: `min(${panelSize.value.width}px, calc(100vw - 32px))`, height: `min(${panelSize.value.height}px, calc(100vh - 32px))` }
  : undefined);

function startResize(event: PointerEvent): void {
  const handle = event.currentTarget as HTMLElement;
  const panel = handle.parentElement as HTMLElement;
  const start = { x: event.clientX, y: event.clientY, width: panel.offsetWidth, height: panel.offsetHeight };
  handle.setPointerCapture(event.pointerId);
  event.preventDefault();

  const onMove = (move: PointerEvent) => {
    panelSize.value = {
      width: clamp(start.width + start.x - move.clientX, MIN_WIDTH, window.innerWidth - 32),
      height: clamp(start.height + start.y - move.clientY, MIN_HEIGHT, window.innerHeight - 32),
    };
  };
  const onUp = () => {
    handle.removeEventListener('pointermove', onMove);
    handle.removeEventListener('pointerup', onUp);
    handle.removeEventListener('pointercancel', onUp);
    saveSize(panelSize.value);
  };
  handle.addEventListener('pointermove', onMove);
  handle.addEventListener('pointerup', onUp);
  handle.addEventListener('pointercancel', onUp);
}

function resetSize(): void {
  panelSize.value = null;
  saveSize(null);
}

function clamp(value: number, min: number, max: number): number {
  return Math.round(Math.min(Math.max(value, min), Math.max(min, max)));
}

function readSavedSize(): { width: number; height: number } | null {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SIZE_KEY) ?? 'null') as { width?: unknown; height?: unknown } | null;
    return typeof saved?.width === 'number' && typeof saved.height === 'number' ? { width: saved.width, height: saved.height } : null;
  } catch {
    return null;
  }
}

function saveSize(size: { width: number; height: number } | null): void {
  try {
    if (size) {
      window.localStorage.setItem(SIZE_KEY, JSON.stringify(size));
    } else {
      window.localStorage.removeItem(SIZE_KEY);
    }
  } catch {
    // Blocked storage: the size simply resets on reload.
  }
}

watch(
  () => [ messages.value.length, messages.value.at(-1)?.text, showEscalation.value ],
  () => nextTick(() => scroller.value?.scrollTo({ top: scroller.value.scrollHeight })),
);
</script>

<style scoped>
.ws-assistant {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1500;
}

.ws-assistant.ws-assistant--embedded {
  inset: 0;
}

.ws-assistant--embedded .ws-assistant-panel {
  width: 100% !important;
  height: 100% !important;
  border-radius: 0;
}

.ws-assistant-panel {
  position: relative;
  width: min(400px, calc(100vw - 32px));
  height: min(620px, calc(100vh - 96px));
}

.ws-assistant-resize {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  touch-action: none;
  background: linear-gradient(135deg, transparent 0 6px, #94a3b8 6px 7.5px, transparent 7.5px 10px, #94a3b8 10px 11.5px, transparent 11.5px);
}

.ws-assistant-resize:hover {
  background: linear-gradient(135deg, transparent 0 6px, #475569 6px 7.5px, transparent 7.5px 10px, #475569 10px 11.5px, transparent 11.5px);
}

.ws-assistant-body {
  overflow-y: auto;
}

.ws-assistant-disclosure {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
}

@media (max-width: 600px) {
  .ws-assistant {
    right: 8px;
    bottom: 8px;
  }

  .ws-assistant-panel {
    width: calc(100vw - 16px) !important;
    height: calc(100vh - 72px) !important;
  }

  .ws-assistant-resize {
    display: none;
  }
}
</style>
